import { Reveal } from "@/components/Reveal";
import { SectionLabel, CTAGroup } from "@/components/bits";
import { IMAGES, PARTNERS } from "@/lib/site";

const VALUES = [
  { index: "01", title: "Discretion", text: "The best technology is invisible. We conceal the hardware so the architecture always leads." },
  { index: "02", title: "Craft", text: "Clean rack builds, labeled wiring, and calibrated rooms — the details you never see are the ones that last." },
  { index: "03", title: "Partnership", text: "We work as an extension of your architect, builder, and design team, from first plans to final walkthrough." },
  { index: "04", title: "Longevity", text: "Systems engineered to evolve, backed by proactive monitoring and white-glove service for years to come." },
];

export default function About() {
  return (
    <div data-testid="page-about" className="pt-32">
      <header className="mx-auto max-w-[1600px] px-6 pb-16 md:px-12">
        <Reveal>
          <SectionLabel>About</SectionLabel>
          <h1 className="mt-8 max-w-5xl font-serif text-5xl font-300 leading-[0.98] md:text-8xl">
            Technology, <span className="italic accent-text">architecturally</span> considered.
          </h1>
        </Reveal>
      </header>

      <section className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 px-6 pb-28 md:px-12 lg:grid-cols-12 lg:gap-20">
        <Reveal className="lg:col-span-6">
          <div className="hover-media relative aspect-[4/5] overflow-hidden border border-white/10">
            <img src={IMAGES.livingWindow} alt="Integrated luxury interior" className="h-full w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 lg:pt-10">
          <p className="font-serif text-3xl font-300 leading-snug text-white/90 md:text-4xl">
            Integrated Solutions AV designs and integrates the systems that make luxury homes effortless — from whole-home automation to private cinemas.
          </p>
          <p className="mt-8 text-sm leading-relaxed text-white/60 md:text-base">
            We partner with luxury homeowners, custom-home builders, and interior designers across Santa Barbara, Los Angeles, and Orange County. Our work is quiet by design: refined interfaces, concealed infrastructure, and dependable performance that respects the architecture and the interiors it lives within.
          </p>
          <p className="mt-6 text-sm leading-relaxed text-white/60 md:text-base">
            As certified integrators for the industry's leading platforms, we bring engineering rigor and a designer's sensibility to every residence — so the technology feels less like a system and more like a natural extension of the home.
          </p>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Licensed low-voltage integrator · CSLB #1152030</p>
        </Reveal>
      </section>

      <section className="border-y border-white/10 bg-isav-bg2 py-28" data-testid="about-values">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Reveal><SectionLabel>How we work</SectionLabel></Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.index} delay={i * 0.08} className="bg-isav-bg2 p-10">
                <span className="font-mono text-sm text-isav-green">{v.index}</span>
                <h3 className="mt-6 font-serif text-2xl">{v.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/55">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28" data-testid="about-partners">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Reveal>
            <SectionLabel>Technology partners</SectionLabel>
            <div className="mt-12 flex flex-wrap items-center gap-x-14 gap-y-8">
              {PARTNERS.map((p) => (
                <span key={p} className="font-serif text-4xl font-300 text-white/40 transition-colors hover:text-white md:text-6xl">{p}</span>
              ))}
            </div>
          </Reveal>
          <div className="mt-20"><CTAGroup /></div>
        </div>
      </section>
    </div>
  );
}
